import { Box, Flex, Skeleton, Textarea } from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";

import { RanaLoading } from "@nens/ranalib";
import { RanaMarkdown } from "@nens/ranalib";

import { EditCancelSubmitButtons } from "@/components/edit-cancel-submit-buttons";
import { HeaderBox, HeaderBoxProps } from "@/components/header-box";
import { MarkdownExplanation } from "@/components/markdown-explanation";
import { RanaRouteBlocker } from "@/components/rana-route-blocker";
import { useProjectActionAllowed } from "@/hooks/use-action-allowed-in-project";
import { FileDataTypeEnum } from "@/lib/rana-file-data-type";
import { ProjectFile } from "@/types/api-model-simple-names";

import { useFilePreviewEdit } from "../hooks/use-file-preview-edit";

// Needs to be called seperatly to get access to the Editable context
function EditControls({
  isEditing,
  isPreviewing,
  startEditing,
  startPreviewing,
  cancelEditing,
  submit,
  fileIsBeingUploaded,
}: ReturnType<typeof useFilePreviewEdit>) {
  const { canWriteProject } = useProjectActionAllowed();

  return (
    <Flex>
      <MarkdownExplanation />
      {canWriteProject && (
        <EditCancelSubmitButtons
          isEditing={isEditing}
          isPreviewing={isPreviewing}
          isMutating={!!fileIsBeingUploaded}
          previewButtonProps={{ onClick: startPreviewing }}
          submitButtonProps={{ onClick: submit }}
          cancelButtonProps={{ onClick: cancelEditing }}
          editButtonProps={{ onClick: startEditing }}
        />
      )}
    </Flex>
  );
}

type MarkdownPreviewProps = ProjectFilePreviewEditProps & ReturnType<typeof useFilePreviewEdit>;

function MarkdownPreview({ file, text }: MarkdownPreviewProps) {
  if (file.dataType === FileDataTypeEnum.Code) {
    text = "``` \n" + text + "\n ```";
  }

  return (
    <Box overflowY="auto" maxH="full" maxW="full" p={6}>
      <RanaMarkdown markdown={text} />
    </Box>
  );
}

interface ProjectFilePreviewEditProps extends Partial<HeaderBoxProps> {
  file: ProjectFile;
}

export function ProjectFilePreviewEdit({ file, ...rest }: ProjectFilePreviewEditProps) {
  const hookReturn = useFilePreviewEdit(file);

  const { isLoading, isSuccess, fileIsBeingUploaded, text, setText, isEditing, isPreviewing } =
    hookReturn;

  if (isLoading) return <HeaderBox name={`\u200B`} as={Skeleton} />;

  if (!isSuccess || text === undefined)
    return (
      <HeaderBox name={`\u200B`} {...rest}>
        <FormattedMessage
          id="heading.text_not_correct"
          defaultMessage="Requesting the text file failed or the text file is binary"
        />
      </HeaderBox>
    );

  return (
    <HeaderBox
      name={<FormattedMessage id="filepreview.edit" defaultMessage="Edit" />}
      {...rest}
      button={<EditControls {...hookReturn} />}
    >
      <Box h="full" w="full">
        {(() => {
          if (fileIsBeingUploaded) return <RanaLoading isCentered h="full" />;

          if (isEditing && !isPreviewing)
            return (
              <>
                <RanaRouteBlocker
                  when={isEditing}
                  message={
                    <FormattedMessage
                      id="filepreview.leave_page_warning"
                      defaultMessage="You have unsaved changes. Are you sure you want to leave?"
                    />
                  }
                />

                <Textarea
                  onChange={(e) => setText(e.target.value)}
                  resize="none"
                  _focusVisible={{ boxShadow: "" }}
                  h="full"
                  overflow="visible"
                  p={6}
                  borderWidth={0}
                  value={text}
                  m={0}
                />
              </>
            );

          return <MarkdownPreview {...hookReturn} file={file} />;
        })()}
      </Box>
    </HeaderBox>
  );
}
